import { useCallback, useState } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { applyProblemErrors, flattenPaths } from './server-errors';

/**
 * Runs a mutation from a form submit: maps `errors[].path` onto the fields, keeps the rest for the
 * `ProblemBanner`, and returns the result (or `undefined` when it failed).
 *
 *   const submit = useFormSubmit(form);
 *   form.handleSubmit(async (values) => {
 *     if (await submit.run(() => create.mutateAsync(values))) onClose();
 *   })
 */
export function useFormSubmit<
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(form: UseFormReturn<TFieldValues, TContext, TTransformedValues>) {
  const [error, setError] = useState<unknown>(null);
  const [omitPaths, setOmitPaths] = useState<ReadonlySet<string>>(() => new Set());
  const [busy, setBusy] = useState(false);

  const run = useCallback(
    async <T>(action: () => Promise<T>): Promise<T | undefined> => {
      setError(null);
      setBusy(true);
      try {
        return await action();
      } catch (caught) {
        const known = flattenPaths(form.getValues());
        const mapped = applyProblemErrors(
          caught,
          (path, message) => form.setError(path as Path<TFieldValues>, { type: 'server', message }),
          known,
        );
        setOmitPaths(mapped);
        setError(caught);
        return undefined;
      } finally {
        setBusy(false);
      }
    },
    [form],
  );

  const reset = useCallback(() => {
    setError(null);
    setOmitPaths(new Set());
  }, []);

  return { run, error, omitPaths, busy, reset };
}
